import React, { useEffect, useState } from "react";
import { PageHeader } from "./Pengumuman";
import { Settings, Image as ImageIcon, Palette, Type, RotateCcw, Save, Handshake, Plus, Edit3, Trash2, Loader2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { API, sponsorAPI } from "../lib/api";

const PRESETS = [
  { name: "Emerald & Gold", primary: "#047857", primaryDark: "#065f46", accent: "#f59e0b" },
  { name: "Ocean Blue", primary: "#0369a1", primaryDark: "#075985", accent: "#38bdf8" },
  { name: "Sunset Orange", primary: "#c2410c", primaryDark: "#9a3412", accent: "#fbbf24" },
  { name: "Royal Purple", primary: "#6d28d9", primaryDark: "#5b21b6", accent: "#f472b6" },
  { name: "Forest Teal", primary: "#0f766e", primaryDark: "#115e59", accent: "#facc15" },
  { name: "Merah Maroon", primary: "#b91c1c", primaryDark: "#991b1b", accent: "#facc15" },
];

const Pengaturan = () => {
  const { theme, updateTheme, resetTheme } = useApp();
  const [local, setLocal] = useState(theme);
  const [logoPreview, setLogoPreview] = useState(theme.logo);

  const [uploading, setUploading] = useState(null); // key currently uploading (for spinner)

  const uploadToStorage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    // r.data.url is a relative /api/files/... path
    return r.data.url;
  };

  const handleFile = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Ukuran gambar maksimal 5MB"); return; }
    setUploading(key);
    try {
      const url = await uploadToStorage(file);
      setLocal((l) => ({ ...l, [key]: url }));
      if (key === "logo") setLogoPreview(url);
      toast.success("Gambar berhasil diunggah");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload gagal");
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  const save = async () => {
    try { await updateTheme(local); toast.success("Pengaturan berhasil disimpan"); }
    catch { toast.error("Gagal menyimpan"); }
  };
  const applyPreset = (p) => setLocal((l) => ({ ...l, primary: p.primary, primaryDark: p.primaryDark, accent: p.accent }));

  // ===== Sponsors =====
  const emptySp = { nama: "", logo: "", url: "", startDate: "", endDate: "", active: true };
  const [sponsors, setSponsors] = useState([]);
  const [spOpen, setSpOpen] = useState(false);
  const [spEditing, setSpEditing] = useState(null);
  const [spForm, setSpForm] = useState(emptySp);

  const loadSponsors = () => sponsorAPI.list().then(setSponsors).catch(() => {});
  useEffect(() => { loadSponsors(); }, []);

  const spReadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo maksimal 2MB"); return; }
    setUploading("sp-logo");
    try {
      const url = await uploadToStorage(file);
      setSpForm((f) => ({ ...f, logo: url }));
    } catch (err) { toast.error(err.response?.data?.detail || "Upload gagal"); }
    finally { setUploading(null); e.target.value = ""; }
  };

  const spOpenNew = () => { setSpEditing(null); setSpForm(emptySp); setSpOpen(true); };
  const spOpenEdit = (s) => { setSpEditing(s); setSpForm({ ...emptySp, ...s }); setSpOpen(true); };
  const spSave = async () => {
    if (!spForm.nama || !spForm.logo || !spForm.startDate || !spForm.endDate) {
      toast.error("Nama, logo, tanggal mulai & berakhir wajib diisi"); return;
    }
    try {
      if (spEditing) { await sponsorAPI.update(spEditing.id, spForm); toast.success("Sponsor diperbarui"); }
      else { await sponsorAPI.create(spForm); toast.success("Sponsor ditambahkan"); }
      setSpOpen(false); loadSponsors();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const spRemove = async (s) => {
    if (!window.confirm(`Hapus sponsor "${s.nama}"?`)) return;
    try { await sponsorAPI.remove(s.id); toast.success("Sponsor dihapus"); loadSponsors(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const sponsorStatus = (s) => {
    const today = new Date().toISOString().slice(0, 10);
    if (!s.active) return { label: "Nonaktif", cls: "bg-slate-100 text-slate-600" };
    if (today < s.startDate) return { label: "Belum Mulai", cls: "bg-amber-100 text-amber-800" };
    if (today > s.endDate) return { label: "Kadaluarsa", cls: "bg-red-100 text-red-700" };
    return { label: "Aktif", cls: "bg-emerald-100 text-emerald-800" };
  };

  return (
    <div>
      <PageHeader title="Pengaturan Aplikasi" subtitle="Kustomisasi tampilan aplikasi (khusus Super Admin)" icon={Settings}
        action={<div className="flex gap-2"><Button variant="outline" onClick={async () => { await resetTheme(); setLocal(theme); toast.success("Direset ke default"); }}><RotateCcw className="h-4 w-4 mr-1" />Reset</Button><Button className="brand-btn" onClick={save}><Save className="h-4 w-4 mr-1" />Simpan</Button></div>} />
      <Tabs defaultValue="branding">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="branding" data-testid="tab-branding"><ImageIcon className="h-4 w-4 mr-1" />Logo & Icon</TabsTrigger>
          <TabsTrigger value="hero" data-testid="tab-hero"><ImageIcon className="h-4 w-4 mr-1" />Gambar Hero</TabsTrigger>
          <TabsTrigger value="header" data-testid="tab-header"><Type className="h-4 w-4 mr-1" />Header & Teks</TabsTrigger>
          <TabsTrigger value="info" data-testid="tab-info"><Type className="h-4 w-4 mr-1" />Info Warga & Footer</TabsTrigger>
          <TabsTrigger value="theme" data-testid="tab-theme"><Palette className="h-4 w-4 mr-1" />Tema Warna</TabsTrigger>
          <TabsTrigger value="sponsors" data-testid="tab-sponsors"><Handshake className="h-4 w-4 mr-1" />Sponsor</TabsTrigger>
        </TabsList>
        <TabsContent value="hero">
          <Card><CardContent className="p-6">
            <h3 className="font-bold mb-1">Header Gambar Beranda (Hero Image)</h3>
            <p className="text-xs text-slate-500 mb-4">Gambar besar yang muncul di halaman utama (Beranda / Login). Rekomendasi resolusi minimal 1920×1080 landscape. Maks 5MB. <span className="text-emerald-700 font-semibold">Disimpan permanen di Emergent Object Storage — tidak hilang setelah re-publish.</span></p>
            {local.heroImage && <div className="mb-4 rounded-xl overflow-hidden border shadow-sm"><img src={local.heroImage} alt="hero" className="w-full max-h-72 object-cover" /></div>}
            <input type="file" accept="image/*" id="hero" className="hidden" onChange={(e) => handleFile(e, "heroImage")} disabled={uploading === "heroImage"} />
            <Label htmlFor="hero" className={`inline-block cursor-pointer px-4 py-2 rounded-lg brand-btn text-white text-sm font-semibold ${uploading === "heroImage" ? "opacity-60 pointer-events-none" : ""}`} data-testid="hero-upload-label">
              {uploading === "heroImage" ? <><Loader2 className="h-4 w-4 mr-1 inline-block animate-spin" />Mengunggah ke Object Storage...</> : <><ImageIcon className="h-4 w-4 mr-1 inline-block" />Unggah Gambar Hero Baru</>}
            </Label>
            <div className="mt-4"><Label className="text-xs">Atau paste URL gambar:</Label><Input value={local.heroImage || ""} onChange={(e) => setLocal({ ...local, heroImage: e.target.value })} placeholder="https://..." /></div>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="info">
          <Card><CardContent className="p-6 space-y-4">
            <h3 className="font-bold">Info Bank & Kontak Penting</h3>
            <p className="text-xs text-slate-500">Ditampilkan di halaman Beranda (Login) untuk warga.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Nama Bank</Label><Input value={local.bankName || ""} onChange={(e) => setLocal({ ...local, bankName: e.target.value })} placeholder="Bank Syariah Indonesia (BSI)" /></div>
              <div><Label>Nomor Rekening</Label><Input value={local.bankAccount || ""} onChange={(e) => setLocal({ ...local, bankAccount: e.target.value })} placeholder="7090123456" /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Atas Nama Rekening</Label><Input value={local.bankHolder || ""} onChange={(e) => setLocal({ ...local, bankHolder: e.target.value })} placeholder="Warga RT DMR" /></div>
              <div><Label>Nomor Pos Satpam</Label><Input value={local.posSatpamPhone || ""} onChange={(e) => setLocal({ ...local, posSatpamPhone: e.target.value })} placeholder="0812-xxxx-xxxx" /></div>
            </div>
            <div className="pt-3 border-t">
              <Label>Teks Footer (muncul di bawah semua halaman)</Label>
              <Textarea rows={2} value={local.footerText || ""} onChange={(e) => setLocal({ ...local, footerText: e.target.value })} placeholder="COPYRIGHT & DEVELOPMENT by. ..." />
            </div>
            <div className="rounded-xl border-2 border-dashed border-emerald-200 p-4 bg-emerald-50/30">
              <div className="text-xs text-emerald-700 font-semibold mb-2">PREVIEW FOOTER</div>
              <div className="text-xs text-slate-600">{local.footerText}</div>
            </div>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="branding">
          <div className="grid md:grid-cols-2 gap-5">
            <Card><CardContent className="p-6">
              <h3 className="font-bold mb-1">Logo Aplikasi</h3><p className="text-xs text-slate-500 mb-4">Ditampilkan di header, sidebar, dan halaman login. Format PNG/JPG, maks 2MB.</p>
              <div className="flex items-center gap-4 mb-4"><img src={logoPreview} alt="logo" className="h-24 w-24 rounded-full object-cover shadow-lg border-4 border-emerald-50" /><div><input type="file" accept="image/*" id="logo" className="hidden" onChange={(e) => handleFile(e, "logo")} disabled={uploading === "logo"} /><Label htmlFor="logo" className={`inline-block cursor-pointer px-4 py-2 rounded-lg brand-btn text-white text-sm font-semibold ${uploading === "logo" ? "opacity-60 pointer-events-none" : ""}`}>{uploading === "logo" ? <><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Mengunggah...</> : "Unggah Logo Baru"}</Label></div></div>
            </CardContent></Card>
            <Card><CardContent className="p-6">
              <h3 className="font-bold mb-1">Favicon (Icon Browser)</h3><p className="text-xs text-slate-500 mb-4">Ikon yang muncul di tab browser. Sebaiknya 64×64 atau 128×128 px.</p>
              <div className="flex items-center gap-4 mb-4"><img src={local.favicon} alt="favicon" className="h-16 w-16 rounded-lg object-cover shadow border" /><div><input type="file" accept="image/*" id="favicon" className="hidden" onChange={(e) => handleFile(e, "favicon")} disabled={uploading === "favicon"} /><Label htmlFor="favicon" className={`inline-block cursor-pointer px-4 py-2 rounded-lg brand-outline-btn text-sm font-semibold ${uploading === "favicon" ? "opacity-60 pointer-events-none" : ""}`}>{uploading === "favicon" ? <><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Mengunggah...</> : "Unggah Favicon"}</Label></div></div>
            </CardContent></Card>
          </div>
        </TabsContent>
        <TabsContent value="header">
          <Card><CardContent className="p-6 space-y-4">
            <div><Label>Nama Aplikasi</Label><Input value={local.appName} onChange={(e) => setLocal({ ...local, appName: e.target.value })} /></div>
            <div><Label>Tagline / Motto</Label><Input value={local.appTagline} onChange={(e) => setLocal({ ...local, appTagline: e.target.value })} /></div>
            <div><Label>Judul Header (ditampilkan besar di atas)</Label><Input value={local.headerTitle} onChange={(e) => setLocal({ ...local, headerTitle: e.target.value })} /></div>
            <div><Label>Teks Sambutan</Label><Textarea rows={3} value={local.welcomeText} onChange={(e) => setLocal({ ...local, welcomeText: e.target.value })} /></div>
            <div className="rounded-xl border-2 border-dashed border-emerald-200 p-4 bg-emerald-50/30">
              <div className="text-xs text-emerald-700 font-semibold mb-2">PREVIEW HEADER</div>
              <div className="font-bold text-slate-800 text-lg" style={{ fontFamily: "Poppins, sans-serif" }}>{local.headerTitle}</div>
              <div className="text-xs text-amber-600 font-semibold tracking-widest">{local.appTagline}</div>
            </div>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="theme">
          <Card><CardContent className="p-6">
            <h3 className="font-bold mb-3">Preset Tema</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              {PRESETS.map((p) => (
                <button key={p.name} onClick={() => applyPreset(p)} className={`p-3 rounded-xl border-2 text-left transition ${local.primary === p.primary ? "border-emerald-500 shadow-md" : "border-slate-200 hover:border-emerald-300"}`}>
                  <div className="flex gap-1 mb-2">
                    <div className="h-6 w-6 rounded" style={{ background: p.primary }} />
                    <div className="h-6 w-6 rounded" style={{ background: p.primaryDark }} />
                    <div className="h-6 w-6 rounded" style={{ background: p.accent }} />
                  </div>
                  <div className="text-sm font-semibold text-slate-800">{p.name}</div>
                </button>
              ))}
            </div>
            <h3 className="font-bold mb-3">Kustom Warna</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {[{ k: "primary", label: "Primer" }, { k: "primaryDark", label: "Primer Gelap" }, { k: "accent", label: "Aksen" }].map((c) => (
                <div key={c.k}><Label>{c.label}</Label><div className="flex gap-2 mt-1"><input type="color" value={local[c.k]} onChange={(e) => setLocal({ ...local, [c.k]: e.target.value })} className="h-10 w-14 rounded cursor-pointer border" /><Input value={local[c.k]} onChange={(e) => setLocal({ ...local, [c.k]: e.target.value })} /></div></div>
              ))}
            </div>
            <div className="mt-6 rounded-xl p-6" style={{ background: `linear-gradient(135deg, ${local.primary}, ${local.primaryDark})` }}>
              <div className="text-white font-bold text-lg" style={{ fontFamily: "Poppins, sans-serif" }}>Preview Tema Warna</div>
              <div className="text-white/80 text-sm mt-1">Gradient primer akan digunakan pada tombol, sidebar aktif, dan header dashboard.</div>
              <button className="mt-3 px-4 py-2 rounded-lg font-semibold text-sm" style={{ background: local.accent, color: "#fff" }}>Tombol Aksen</button>
            </div>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="sponsors">
          <Card><CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-slate-800">Sponsor / Iklan di Beranda</h3>
                <p className="text-xs text-slate-500 mt-1">Logo sponsor yang periode kontraknya aktif otomatis tampil di halaman Beranda.</p>
              </div>
              <Button className="brand-btn" onClick={spOpenNew} data-testid="sponsor-add-btn"><Plus className="h-4 w-4 mr-1" />Tambah Sponsor</Button>
            </div>
            {sponsors.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-slate-200 py-10 text-center text-slate-400">
                Belum ada sponsor terdaftar. Klik "Tambah Sponsor" untuk memulai.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {sponsors.map((s) => {
                  const st = sponsorStatus(s);
                  return (
                    <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 flex gap-3 hover:shadow-md transition" data-testid={`sponsor-card-${s.id}`}>
                      <img src={s.logo} alt={s.nama} className="h-16 w-16 object-contain rounded-md bg-slate-50 border" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-bold text-slate-800 truncate">{s.nama}</div>
                          <Badge className={`${st.cls} hover:${st.cls}`}>{st.label}</Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{s.startDate} — {s.endDate}</div>
                        {s.url && <div className="text-[10px] text-blue-600 truncate mt-1">{s.url}</div>}
                        <div className="flex gap-1 mt-2">
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => spOpenEdit(s)}><Edit3 className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => spRemove(s)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent></Card>

          <Dialog open={spOpen} onOpenChange={setSpOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle>{spEditing ? "Edit Sponsor" : "Tambah Sponsor"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nama Sponsor *</Label><Input value={spForm.nama} onChange={(e) => setSpForm({ ...spForm, nama: e.target.value })} placeholder="Contoh: Toko Berkah Jaya" /></div>
                <div>
                  <Label>Logo *</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {spForm.logo ? <img src={spForm.logo} alt="logo" className="h-16 w-24 object-contain rounded bg-slate-50 border" /> : <div className="h-16 w-24 rounded border-2 border-dashed grid place-items-center text-slate-400"><ImageIcon className="h-5 w-5" /></div>}
                    <div>
                      <input type="file" accept="image/*" id="sp-logo" className="hidden" onChange={spReadFile} />
                      <Label htmlFor="sp-logo" className="cursor-pointer inline-block px-3 py-2 rounded-md brand-btn text-white text-sm">Unggah Logo</Label>
                    </div>
                  </div>
                  <Input value={spForm.logo || ""} onChange={(e) => setSpForm({ ...spForm, logo: e.target.value })} placeholder="Atau paste URL logo..." className="mt-2" />
                </div>
                <div><Label>URL Website (opsional)</Label><Input value={spForm.url} onChange={(e) => setSpForm({ ...spForm, url: e.target.value })} placeholder="https://..." /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tanggal Mulai *</Label><Input type="date" value={spForm.startDate} onChange={(e) => setSpForm({ ...spForm, startDate: e.target.value })} /></div>
                  <div><Label>Tanggal Berakhir *</Label><Input type="date" value={spForm.endDate} onChange={(e) => setSpForm({ ...spForm, endDate: e.target.value })} /></div>
                </div>
                <label className="flex items-center gap-2 text-sm mt-2">
                  <input type="checkbox" checked={spForm.active} onChange={(e) => setSpForm({ ...spForm, active: e.target.checked })} className="h-4 w-4 accent-emerald-600" />
                  <span>Aktif (tampil di beranda dalam periode kontrak)</span>
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSpOpen(false)}>Batal</Button>
                <Button className="brand-btn" onClick={spSave} data-testid="sponsor-save-btn">{spEditing ? "Simpan" : "Tambah"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};
export default Pengaturan;
